import axios from "axios";
import { eventBus } from "@/main";
import {
  FILE_LIMIT_EXCEED,
  FILE_STORAGE_PLAN_EXPIRED,
  REDIIVIEW_BASE_URL
} from "@/constants";

// What the server can produce a thumbnail from. Everything else stored as a
// media file (PDF, video, 3D models) keeps its own placeholder in the UI.
const THUMBNAILABLE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp"
];

// Small enough that the first tiles appear quickly, and within the server's
// per-request cap.
const THUMBNAIL_CHUNK = 6;

const moduleMediaFile = {
  namespaced: true,
  state: {
    fileList: [],
    fileTemplate: [],
    fileDetail: {},
    newFilesLength: 0
  },
  mutations: {
    setFileList(state, data) {
      state.fileList = data;
    },
    concatFileList(state, data) {
      state.fileList = [...state.fileList, ...data];
    },
    setThumbnail(state, { id, url }) {
      const file = state.fileList.find(f => f.id === id);
      if (file) file.thumb = url;
    },
    setFileTemplate(state, data) {
      state.fileTemplate = data;
    },
    concatFileTemplate(state, data) {
      state.fileTemplate = [...state.fileTemplate, ...data];
    },
    setFileDetail(state, data) {
      state.fileDetail = data;
    },
    RESET_FILE_LIST(state) {
      state.fileList = [];
    },
    setNewFileLength(state, data) {
      state.newFilesLength = data;
    }
  },
  actions: {
    async createMediaFiles({ commit, dispatch }, payload) {
      try {
        const urls = await dispatch("generatePresingUrl", payload);
        payload.urls = urls.map(i => i.url);
        const res = await dispatch("uploadFilesToS3", payload);
        return res;
      } catch (err) {
        const msg = err.response?.data.errorMessage;
        if (msg === "file limit exceeded")
          eventBus.$emit("alertSwal", FILE_LIMIT_EXCEED);
        if (msg === "file store plan expired.")
          eventBus.$emit("alertSwal", FILE_STORAGE_PLAN_EXPIRED);

        // Automatic error handling via interceptor
        throw err;
      }
    },
    async generatePresingUrl({ commit }, payload) {
      return await axios
        .post(
          `/api/mediaFile/getPresigned/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.data
        )
        .then(res => {
          return res.data;
        })
        .catch(err => {
          // // Automatic error handling via interceptor
          throw err;
        });
    },
    async uploadFilesToS3({ commit }, { files, urls }) {
      const results = [];
      for (let i = 0; i < files.length; i++) {
        try {
          const result = await axios.put(urls[i], files[i], {
            transformRequest: (data, headers) => {
              delete headers["Authorization"];
              return data;
            },
            headers: {
              "Content-Type": files[i].type
            }
          });
          results.push(result);
        } catch (err) {
          results.push(null); // or any other error handling mechanism
          // Automatic error handling via interceptor
          throw err;
        }
      }
      return results;
    },
    async fecthMediaFiles({ commit, dispatch, getters }, payload) {
      const concat = payload.params.concat;
      delete payload.params.concat;

      const ephemeral = payload.params.ephemeral;
      delete payload.params.ephemeral;

      // Handle soft deleted parameter for recyclebin
      const softDeleted = payload.params.softDeleted;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        { params: payload.params }
      );
      const data = getters.getMediaFileListFormat(res.data);
      if (ephemeral) return data;
      if (concat) {
        commit("concatFileList", data);
        dispatch("resolveThumbnails", { ...payload, files: data });
        return;
      }
      commit("setFileList", data);
      dispatch("resolveThumbnails", { ...payload, files: data });
      return data;
    },

    /**
     * Fills in thumbnails for files that do not have one yet.
     *
     * Deliberately not awaited by the caller: the list renders immediately
     * from the full-size URLs it already has, and each tile swaps to its
     * thumbnail as the answer arrives. Generating one reads a multi-megabyte
     * original server-side, so this asks in small chunks rather than for a
     * whole page at once.
     */
    async resolveThumbnails({ commit }, { clinicUrl, branchUrl, files }) {
      // Only images can be thumbnailed. Asking for PDFs and videos would
      // return null every time and re-ask on every page view.
      const pending = (files || [])
        .filter(f => !f.thumb && THUMBNAILABLE_TYPES.includes(f.fileType))
        .map(f => f.id);

      if (pending.length === 0) return;

      for (let i = 0; i < pending.length; i += THUMBNAIL_CHUNK) {
        const chunk = pending.slice(i, i + THUMBNAIL_CHUNK);

        try {
          const res = await axios.post(
            `/api/mediaFile/thumbnails/${clinicUrl}/${branchUrl}`,
            { ids: chunk }
          );
          const urls = (res.data && res.data.urls) || {};

          chunk.forEach(id => {
            if (urls[id]) commit("setThumbnail", { id, url: urls[id] });
          });
        } catch (error) {
          // A tile that keeps its full-size URL still displays correctly, so
          // a failure here costs bandwidth, not function.
          break;
        }
      }
    },
    async fecthMediaFilesTemplate({ commit, getters }, payload) {
      const concat = payload.params?.concat;
      delete payload.params?.concat;

      const ephemeral = payload.params?.ephemeral;
      delete payload.params?.ephemeral;

      const res = await axios.get(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}`,
        {
          params: { category: "Template", ...payload.params }
        }
      );
      const data = getters.getMediaFileListFormat(res.data);
      if (ephemeral) return data;
      if (concat) {
        commit("concatFileTemplate", data);
        return;
      }
      commit("setFileTemplate", data);
      return data;
    },
    async deleteMediaFile({ commit, state }, payload) {
      // Soft delete - move to recyclebin
      const res = await axios.delete(
        `/api/mediaFile/recycleBin/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
        const t = state.fileTemplate.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileTemplate", t);
      }
      return res;
    },
    async restoreMediaFile({ commit, state }, payload) {
      const res = await axios.post(
        `/api/mediaFile/restore/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async permanentlyDeleteMediaFile({ commit, state }, payload) {
      // Using the regular delete endpoint for permanent deletion
      const res = await axios.delete(
        `/api/mediaFile/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
      );
      if (res.status === 200) {
        const f = state.fileList.filter(i => {
          return payload.id !== i.id;
        });
        commit("setFileList", f);
      }
      return res;
    },
    async emptyRecycleBin({ commit, state }, payload) {
      // Update endpoint to match API path: /api/mediaFile/recycleBin/clear/{clinicURL}/{branchURL}
      const res = await axios.delete(
        `/api/mediaFile/clear/recycleBin/${payload.clinicUrl}/${payload.branchUrl}`
      );
      if (res.status === 200) {
        commit("setFileList", []);
      }
      return res;
    },
    async setNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async addNewFileLength({ commit, state }, payload) {
      commit("setNewFileLength", payload);
    },
    async fetchRadiiViewUrl(context, { imageUrl }) {
      return await axios.post(
        REDIIVIEW_BASE_URL,
        { image_url: imageUrl },
        {
          withCredentials: false
        }
      );
    },
    async subscribeFileStorage({ commit }, payload) {
      const res = await axios.post(
        `/api/branch/${payload.clinicUrl}/${payload.branchUrl}/fileStorage/subscribe`,
        {
          durationMonths: payload.durationMonths,
          packageId: payload.packageId,
          confirmed: payload.confirmed !== undefined ? payload.confirmed : true
        }
      );
      return res;
    },
  },
  getters: {
    getFileList: state => {
      return state.fileList;
    },
    getFileTemplate: state => {
      return state.fileTemplate;
    },

    getMediaFileListFormat: (
      state,
      getters,
      rootState,
      rootGetters
    ) => data => {
      return data.map(i => {
        return {
          id: i.id,
          src: i.mediaURL,
          // Grids render `thumb` and fall back to `src`; lightboxes, printing
          // and document embedding always use `src`. Declared even when empty
          // so Vue 2 can react to it being filled in later.
          thumb: i.thumbnailURL || null,
          title: i.remark ? i.remark : i.category,
          category: i.category,
          mediaType: i.fileType === "application/pdf" ? "iframe" : null,
          remark: i.remark,
          creationDt: i.creationDt,
          treatmentId: i.treatmentId,
          fileSize: i.fileSize,
          fileType: i.fileType,
          deletedAt: i.deletedAt  // Include deletedAt field for recycle bin sorting
        };
      });
    }
  }
};
export default moduleMediaFile;
